import { type HTMLAttributes, type Ref } from 'react';
import clsx from 'clsx';
import './Layout.css';

/* ==================== Screen ==================== */

export interface ScreenProps extends HTMLAttributes<HTMLDivElement> {
  ref?: Ref<HTMLDivElement>;
}

/** Page-level shell — full viewport, safe-area insets, brand surface. */
export function Screen({ className, ref, ...rest }: ScreenProps) {
  return <div ref={ref} className={clsx('fluent-screen', className)} {...rest} />;
}

/* ==================== Container ==================== */

export type ContainerSize = 'sm' | 'md' | 'lg' | 'xl';

export interface ContainerProps extends HTMLAttributes<HTMLDivElement> {
  /** Max-width preset. `md` = 560px (default), `lg` = 720px. */
  size?: ContainerSize;
  ref?: Ref<HTMLDivElement>;
}

/** Horizontally-centered, max-width-bounded wrapper for page content. */
export function Container({ size = 'md', className, ref, ...rest }: ContainerProps) {
  return <div ref={ref} className={clsx('fluent-container', className)} data-size={size} {...rest} />;
}

/* ==================== Stack ==================== */

export type SpacingStep = 0 | 1 | 2 | 3 | 4 | 6 | 8 | 12 | 16;
export type StackAlign = 'start' | 'center' | 'end' | 'stretch';

export interface StackProps extends HTMLAttributes<HTMLDivElement> {
  /** Gap in token units (multiplied by 4px). Default 4 = 16px. */
  gap?: SpacingStep;
  align?: StackAlign;
  ref?: Ref<HTMLDivElement>;
}

/** Vertical flex column with uniform gap between children. */
export function Stack({ gap = 4, align, className, ref, ...rest }: StackProps) {
  return (
    <div
      ref={ref}
      className={clsx('fluent-stack', className)}
      data-gap={gap}
      data-align={align}
      {...rest}
    />
  );
}

/* ==================== Row ==================== */

export type RowAlign = 'start' | 'center' | 'end' | 'baseline';
export type RowJustify = 'start' | 'center' | 'end' | 'between' | 'around';

export interface RowProps extends HTMLAttributes<HTMLDivElement> {
  gap?: SpacingStep;
  align?: RowAlign;
  justify?: RowJustify;
  wrap?: boolean;
  ref?: Ref<HTMLDivElement>;
}

/** Horizontal flex row with uniform gap between children. */
export function Row({
  gap = 2,
  align,
  justify,
  wrap,
  className,
  ref,
  ...rest
}: RowProps) {
  return (
    <div
      ref={ref}
      className={clsx('fluent-row', className)}
      data-gap={gap}
      data-align={align}
      data-justify={justify}
      data-wrap={wrap || undefined}
      {...rest}
    />
  );
}
